import React, { useEffect, useState } from 'react';
import { POSLayout } from './components/POSLayout';
import { OrderQueue, type HistoryEvent, type Order, type OrderLine, type OrderStatus } from './components/OrderQueue';
import { OrderDetails } from './components/OrderDetails';
import { OrderHistory } from './components/OrderHistory';
import { DEFAULT_OPERATIONAL_SETTINGS, OperationalSettings, SettingsPanel } from './components/SettingsPanel';
import { Toaster, toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, ChevronRight } from 'lucide-react';
import { useScreenInit } from './useScreenInit.js';
const productImages = {
  panadol: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop',
  amoxicillin: 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=200&h=200&fit=crop',
  vitaminC: 'https://images.unsplash.com/photo-1616671285442-299c27666291?w=200&h=200&fit=crop'
};
function line(overrides: Partial<OrderLine> & Pick<OrderLine, 'itemId' | 'name' | 'tradeDrugCode'>): OrderLine {
  const qty = overrides.qty ?? 1;
  return {
    id: overrides.itemId,
    barcode: '6281234567890',
    sku: overrides.tradeDrugCode,
    genericCode: 'GEN-001',
    qty,
    uom: 'Box',
    unitPrice: 45,
    requiresLot: true,
    allowSubstitution: false,
    instructionsAr: 'حسب وصفة الطبيب',
    pickedQuantity: 0,
    status: 'Pending',
    image: productImages.panadol,
    lotNumbers: [{
      number: 'LOT-A1',
      expiryDate: '2026-12',
      stock: 50
    }],
    isSerialized: false,
    serialNumbers: [],
    ...overrides,
    quantity: overrides.qty ?? 1
  };
}
function orderHistory(status: OrderStatus, message: string, timestamp: string): HistoryEvent[] {
  return [{
    id: 'received',
    timestamp,
    status,
    message,
    actor: 'System',
    actorName: 'Order Gateway'
  }];
}
const INITIAL_ORDERS: Order[] = [{
  id: 'ORD-7721',
  customerName: 'Khalid Mansour',
  customerPhone: '966501234567',
  customer: {
    name: 'Khalid Mansour',
    mobile: '966501234567',
    nationalId: '1023456789',
    gender: 'Male',
    dateOfBirth: '1992-05-14'
  },
  address: {
    line1: 'Building 42, Al-Olaya St',
    city: 'Riyadh',
    district: 'Al-Olaya',
    zipCode: '12211',
    nationalShortAddress: 'RDKA1234'
  },
  lines: [line({
    itemId: 'l1',
    name: 'Panadol Extra 500mg',
    tradeDrugCode: 'PAN-001',
    genericCode: 'PARA-500',
    qty: 2,
    unitPrice: 22.5,
    instructionsAr: 'قرص بعد الأكل عند اللزوم',
    requiresPrescription: true,
    allowSubstitution: true
  }), line({
    itemId: 'l2',
    name: 'Amoxicillin 500mg',
    tradeDrugCode: 'AMX-500',
    genericCode: 'AMOX-500',
    unitPrice: 200,
    image: productImages.amoxicillin,
    isSerialized: true,
    barcode: '6289876543210',
    serialNumbers: [],
    requiresPrescription: true,
    allowSubstitution: true,
    lotNumbers: [{
      number: 'LOT-B1',
      expiryDate: '2027-08',
      stock: 100
    }]
  })],
  itemsCount: 2,
  totalAmount: 245.5,
  status: 'Pending',
  channel: 'Yaser eRx',
  channelPartner: 'Yaser',
  payerType: 'Insurance',
  paymentType: 'Prepaid',
  createdAt: '2026-07-08 10:15 AM',
  acceptanceDeadline: new Date(Date.now() + 8 * 60_000).toISOString(),
  slaMinutes: 8,
  priority: 'High',
  insurance: {
    payerName: 'Bupa Arabia',
    policyNo: 'BUP-2026-8851',
    memberId: 'MEM-991826',
    approvalNo: 'APR-44720',
    copayPercent: 20,
    patientShare: 49.1,
    insuranceShare: 196.4,
    paymentLinkStatus: 'Pending'
  },
  prescription: {
    erxReference: 'YAS-ERX-980021',
    verified: false,
    image: productImages.amoxicillin,
    activities: [{
      genericCode: 'AMOX-500',
      instructionsAr: 'كبسولة كل 8 ساعات',
      durationDays: 7
    }],
    acceptableTradeDrugs: {
      'AMOX-500': ['AMX-500', 'AMOX-PLUS']
    }
  },
  discountAmount: 15,
  couponCode: 'WELCOME15',
  isLoyaltyCustomer: true,
  loyaltyPointsUsed: 500,
  qitafAmount: 20,
  sourceOrderId: 'YAS-99281',
  currentBranch: 'Olaya Branch',
  history: orderHistory('Pending', 'eRx order received and awaiting pharmacist acceptance', '2026-07-08 10:15 AM')
}, {
  id: 'ORD-7722',
  customerName: 'Sara Al-Otaibi',
  customerPhone: '966559876543',
  customer: {
    name: 'Sara Al-Otaibi',
    mobile: '966559876543',
    nationalId: '1076543210',
    gender: 'Female',
    dateOfBirth: '1998-09-02'
  },
  address: {
    line1: 'Villa 12, Al-Malqa',
    city: 'Riyadh',
    district: 'Al-Malqa',
    zipCode: '13521',
    nationalShortAddress: 'RDAA5987'
  },
  lines: [line({
    itemId: 'l3',
    name: 'Vitamin C 1000mg',
    tradeDrugCode: 'VIT-C',
    genericCode: 'ASC-1000',
    unitPrice: 120,
    image: productImages.vitaminC,
    barcode: '6285554443332',
    instructionsAr: 'قرص يومياً'
  })],
  itemsCount: 1,
  totalAmount: 120,
  status: 'Accepted',
  channel: 'Aggregator',
  channelPartner: 'HungerStation',
  payerType: 'Partner Prepaid',
  paymentType: 'Prepaid',
  createdAt: '2026-07-08 10:20 AM',
  acceptanceDeadline: '2026-07-08T10:40:00+03:00',
  slaMinutes: 15,
  priority: 'Medium',
  courierName: 'HungerStation',
  sourceOrderId: 'HG-88271',
  history: orderHistory('Accepted', 'Partner order accepted by pharmacist', '2026-07-08 10:25 AM')
}, {
  id: 'ORD-7723',
  customerName: 'Mohammed Rashid',
  customerPhone: '966543210987',
  customer: {
    name: 'Mohammed Rashid',
    mobile: '966543210987',
    nationalId: '1056589123',
    gender: 'Male',
    dateOfBirth: '1987-01-20'
  },
  address: {
    line1: 'Apt 402, Al-Nakheel Tower',
    city: 'Jeddah',
    district: 'Al-Nakheel',
    zipCode: '23423',
    nationalShortAddress: 'JDKA7022'
  },
  lines: [line({
    itemId: 'l4',
    name: 'Panadol Extra 500mg',
    tradeDrugCode: 'PAN-001',
    genericCode: 'PARA-500',
    qty: 2,
    unitPrice: 45
  }), line({
    itemId: 'l5',
    name: 'Vitamin C 1000mg',
    tradeDrugCode: 'VIT-C',
    genericCode: 'ASC-1000',
    qty: 3,
    unitPrice: 120,
    image: productImages.vitaminC
  })],
  itemsCount: 2,
  totalAmount: 450.75,
  status: 'Picking',
  channel: 'Website',
  payerType: 'Cash',
  paymentType: 'Mada',
  createdAt: '2026-07-08 09:45 AM',
  acceptanceDeadline: '2026-07-08T10:00:00+03:00',
  slaMinutes: 4,
  priority: 'High',
  courierName: 'Careem Box',
  courierPhone: '966920033786',
  discountAmount: 25,
  couponCode: 'HEALTHY2026',
  sourceOrderId: 'WEB-99285',
  history: orderHistory('Picking', 'Pharmacist started picking items', '2026-07-08 09:55 AM')
}, {
  id: 'ORD-7726',
  customerName: 'Omar Farooq',
  customerPhone: '966502223333',
  customer: {
    name: 'Omar Farooq',
    mobile: '966502223333',
    gender: 'Male'
  },
  address: {
    line1: 'Al-Nuzha District',
    city: 'Riyadh',
    district: 'Al-Nuzha',
    zipCode: '12471'
  },
  lines: [line({
    itemId: 'l6',
    name: 'Panadol Extra 500mg',
    tradeDrugCode: 'PAN-001',
    genericCode: 'PARA-500',
    qty: 2,
    pickedQuantity: 2,
    status: 'Picked'
  })],
  itemsCount: 1,
  totalAmount: 180,
  status: 'Delivered',
  channel: 'Website',
  payerType: 'Cash',
  paymentType: 'Prepaid',
  createdAt: '2026-07-07 04:30 PM',
  acceptanceDeadline: '2026-07-07T16:45:00+03:00',
  slaMinutes: 0,
  priority: 'Medium',
  sourceOrderId: 'WEB-99290',
  history: orderHistory('Delivered', 'Order delivered successfully', '2026-07-07 05:15 PM')
}, {
  id: 'ORD-7727',
  customerName: 'Laila Hassan',
  customerPhone: '966554445555',
  customer: {
    name: 'Laila Hassan',
    mobile: '966554445555',
    gender: 'Female'
  },
  address: {
    line1: 'Al-Waha',
    city: 'Riyadh',
    district: 'Al-Waha',
    zipCode: '12444'
  },
  lines: [line({
    itemId: 'l7',
    name: 'Vitamin C 1000mg',
    tradeDrugCode: 'VIT-C',
    genericCode: 'ASC-1000',
    unitPrice: 55,
    image: productImages.vitaminC
  })],
  itemsCount: 1,
  totalAmount: 55,
  status: 'Cancelled',
  channel: 'Aggregator',
  channelPartner: 'Jahez',
  payerType: 'Partner Prepaid',
  paymentType: 'Prepaid',
  createdAt: '2026-07-07 02:00 PM',
  acceptanceDeadline: '2026-07-07T14:10:00+03:00',
  slaMinutes: 0,
  priority: 'Low',
  sourceOrderId: 'JHZ-88275',
  rejectionReason: 'Customer changed mind',
  history: orderHistory('Cancelled', 'Order cancelled: Customer changed mind', '2026-07-07 02:10 PM')
}, {
  id: 'ORD-8801',
  customerName: 'Sami Mansour',
  customerPhone: '966509998888',
  customer: {
    name: 'Sami Mansour',
    mobile: '966509998888',
    nationalId: '1011121314',
    gender: 'Male',
    dateOfBirth: '1984-04-04'
  },
  address: {
    line1: 'Olaya Branch collection desk',
    city: 'Riyadh',
    district: 'Al-Olaya',
    zipCode: '12211',
    nationalShortAddress: 'RDKA9981'
  },
  lines: [line({
    itemId: 'l8',
    name: 'Vitamin C 1000mg',
    tradeDrugCode: 'VIT-C',
    genericCode: 'ASC-1000',
    unitPrice: 45,
    pickedQuantity: 1,
    status: 'Picked',
    image: productImages.vitaminC
  })],
  itemsCount: 1,
  totalAmount: 45,
  status: 'Ready for Pickup',
  channel: 'Walk-in',
  payerType: 'Cash',
  paymentType: 'Mada',
  createdAt: '2026-07-08 11:00 AM',
  acceptanceDeadline: '2026-07-08T11:20:00+03:00',
  slaMinutes: 25,
  priority: 'Medium',
  otp: '5566',
  isLoyaltyCustomer: true,
  loyaltyPointsUsed: 1200,
  walletAmount: 15,
  sourceOrderId: 'POS-10050',
  currentBranch: 'Olaya Branch',
  history: orderHistory('Ready for Pickup', 'Order packed; pickup OTP sent to customer', '2026-07-08 11:15 AM')
}, {
  id: 'ORD-9101',
  customerName: 'Noura Al-Harbi',
  customerPhone: '966507771122',
  customer: {
    name: 'Noura Al-Harbi',
    mobile: '966507771122',
    nationalId: '1018765432',
    gender: 'Female',
    dateOfBirth: '1993-03-18'
  },
  address: {
    line1: 'Apartment 8, King Fahd Road',
    city: 'Riyadh',
    district: 'Al-Wurud',
    zipCode: '12253',
    nationalShortAddress: 'RDWR1122'
  },
  lines: [line({
    itemId: 'l10',
    name: 'Panadol Extra 500mg',
    tradeDrugCode: 'PAN-001',
    genericCode: 'PARA-500',
    qty: 2,
    unitPrice: 24
  })],
  itemsCount: 1,
  totalAmount: 48,
  status: 'Awaiting Partner Courier',
  channel: 'Aggregator',
  channelPartner: 'Jahez',
  payerType: 'Partner Prepaid',
  paymentType: 'Prepaid',
  createdAt: '2026-07-08 11:25 AM',
  acceptanceDeadline: '2026-07-08T11:40:00+03:00',
  slaMinutes: 12,
  priority: 'High',
  courierName: 'Jahez',
  sourceOrderId: 'JHZ-91001',
  history: orderHistory('Awaiting Partner Courier', 'Packed order handed to Jahez dispatch queue', '2026-07-08 11:32 AM')
}, {
  id: 'ORD-9102',
  customerName: 'Faisal Al-Qahtani',
  customerPhone: '966506667700',
  customer: {
    name: 'Faisal Al-Qahtani',
    mobile: '966506667700',
    nationalId: '1045678921',
    gender: 'Male',
    dateOfBirth: '1989-11-06'
  },
  address: {
    line1: 'Olaya Branch collection counter',
    city: 'Riyadh',
    district: 'Al-Olaya',
    zipCode: '12241',
    nationalShortAddress: 'RDOL6700'
  },
  lines: [line({
    itemId: 'l11',
    name: 'Vitamin C 1000mg',
    tradeDrugCode: 'VIT-C',
    genericCode: 'ASC-1000',
    unitPrice: 65,
    image: productImages.vitaminC,
    pickedQuantity: 1,
    status: 'Picked'
  })],
  itemsCount: 1,
  totalAmount: 65,
  status: 'Ready for Pickup',
  channel: 'Walk-in',
  channelPartner: 'HungerStation',
  payerType: 'Partner Prepaid',
  paymentType: 'Prepaid',
  createdAt: '2026-07-08 11:30 AM',
  acceptanceDeadline: '2026-07-08T11:45:00+03:00',
  slaMinutes: 15,
  priority: 'Medium',
  otp: '1024',
  sourceOrderId: 'HS-PICK-91002',
  currentBranch: 'Olaya Branch',
  history: orderHistory('Ready for Pickup', 'HungerStation pickup order packed at collection counter', '2026-07-08 11:36 AM')
}, {
  id: 'ORD-9103',
  customerName: 'Hessa Al-Dosari',
  customerPhone: '966505552211',
  customer: {
    name: 'Hessa Al-Dosari',
    mobile: '966505552211',
    nationalId: '1098765123',
    gender: 'Female',
    dateOfBirth: '1996-07-22'
  },
  address: {
    line1: 'Yaser pickup cabinet, King Abdullah Road',
    city: 'Riyadh',
    district: 'Al-Muruj',
    zipCode: '12281',
    nationalShortAddress: 'RDMR2211'
  },
  lines: [line({
    itemId: 'l12',
    name: 'Amoxicillin 500mg',
    tradeDrugCode: 'AMX-500',
    genericCode: 'AMOX-500',
    unitPrice: 86,
    image: productImages.amoxicillin,
    isSerialized: true,
    barcode: '6289876543210',
    serialNumbers: ['SN-YAS-9103'],
    pickedQuantity: 1,
    status: 'Picked',
    requiresPrescription: true,
    allowSubstitution: true
  })],
  itemsCount: 1,
  totalAmount: 86,
  status: 'Ready for Pickup',
  channel: 'Yaser eRx',
  channelPartner: 'Yaser',
  payerType: 'Cash',
  paymentType: 'Mada',
  createdAt: '2026-07-08 11:35 AM',
  acceptanceDeadline: '2026-07-08T11:50:00+03:00',
  slaMinutes: 15,
  priority: 'High',
  otp: '7719',
  sourceOrderId: 'YAS-PICK-91003',
  currentBranch: 'King Abdullah Branch',
  prescription: {
    erxReference: 'YAS-ERX-91003',
    verified: true,
    activities: [{
      genericCode: 'AMOX-500',
      instructionsAr: 'كبسولة كل ثماني ساعات',
      durationDays: 5
    }],
    acceptableTradeDrugs: {
      'AMOX-500': ['AMX-500', 'AMOX-PLUS']
    }
  },
  pickupVerification: 'nationalId',
  history: orderHistory('Ready for Pickup', 'Verified Yaser eRx prepared for pickup after national ID match', '2026-07-08 11:42 AM')
}, {
  id: 'ORD-9104',
  customerName: 'Abdullah Al-Salem',
  customerPhone: '966509991100',
  customer: {
    name: 'Abdullah Al-Salem',
    mobile: '966509991100',
    nationalId: '1022244446',
    gender: 'Male',
    dateOfBirth: '1978-02-14'
  },
  address: {
    line1: 'House 19, Al-Rawdah',
    city: 'Jeddah',
    district: 'Al-Rawdah',
    zipCode: '23434',
    nationalShortAddress: 'JDRA1100'
  },
  lines: [line({
    itemId: 'l13',
    name: 'Panadol Extra 500mg',
    tradeDrugCode: 'PAN-001',
    genericCode: 'PARA-500',
    qty: 3,
    unitPrice: 25
  })],
  itemsCount: 1,
  totalAmount: 75,
  status: 'Pending',
  channel: 'Website',
  payerType: 'Insurance',
  paymentType: 'Prepaid',
  createdAt: '2026-07-08 11:40 AM',
  acceptanceDeadline: new Date(Date.now() + 15 * 60_000).toISOString(),
  slaMinutes: 15,
  priority: 'Medium',
  sourceOrderId: 'WEB-INS-91004',
  insurance: {
    payerName: 'Tawuniya',
    policyNo: 'TAW-909190',
    memberId: 'MEM-91004',
    approvalNo: 'APR-91004',
    copayPercent: 15,
    patientShare: 11.25,
    insuranceShare: 63.75,
    paymentLinkStatus: 'Pending'
  },
  history: orderHistory('Pending', 'Insurance website order awaiting pharmacist acceptance', '2026-07-08 11:40 AM')
}, {
  id: 'ORD-9105',
  customerName: 'Maha Al-Mutairi',
  customerPhone: '966505551234',
  customer: {
    name: 'Maha Al-Mutairi',
    mobile: '966505551234',
    nationalId: '1067891234',
    gender: 'Female',
    dateOfBirth: '1985-12-09'
  },
  address: {
    line1: 'Riyadh Central branch counter',
    city: 'Riyadh',
    district: 'Al-Sulaimaniyah',
    zipCode: '12243',
    nationalShortAddress: 'RDSU1234'
  },
  lines: [line({
    itemId: 'l14',
    name: 'Vitamin C 1000mg',
    tradeDrugCode: 'VIT-C',
    genericCode: 'ASC-1000',
    unitPrice: 90,
    image: productImages.vitaminC
  })],
  itemsCount: 1,
  totalAmount: 90,
  status: 'Accepted',
  channel: 'Walk-in',
  payerType: 'Insurance',
  paymentType: 'Prepaid',
  createdAt: '2026-07-08 11:45 AM',
  acceptanceDeadline: '2026-07-08T12:00:00+03:00',
  slaMinutes: 15,
  priority: 'Medium',
  currentBranch: 'Riyadh Central',
  insurance: {
    payerName: 'Bupa Arabia',
    policyNo: 'BUP-776622',
    memberId: 'MEM-91005',
    approvalNo: 'APR-91005',
    copayPercent: 10,
    patientShare: 9,
    insuranceShare: 81,
    paymentLinkStatus: 'Paid'
  },
  history: orderHistory('Accepted', 'Walk-in insurance claim approved at counter', '2026-07-08 11:46 AM')
}, {
  id: 'ORD-9106',
  customerName: 'Saleh Al-Anazi',
  customerPhone: '966504443322',
  customer: {
    name: 'Saleh Al-Anazi',
    mobile: '966504443322',
    nationalId: '1033344445',
    gender: 'Male',
    dateOfBirth: '1991-06-25'
  },
  address: {
    line1: 'Riyadh Central branch counter',
    city: 'Riyadh',
    district: 'Al-Sulaimaniyah',
    zipCode: '12243',
    nationalShortAddress: 'RDSU3322'
  },
  lines: [line({
    itemId: 'l15',
    name: 'Panadol Extra 500mg',
    tradeDrugCode: 'PAN-001',
    genericCode: 'PARA-500',
    qty: 2,
    unitPrice: 30
  })],
  itemsCount: 1,
  totalAmount: 60,
  status: 'Pending',
  channel: 'Walk-in',
  payerType: 'Cash',
  paymentType: 'COD',
  createdAt: '2026-07-08 11:50 AM',
  acceptanceDeadline: new Date(Date.now() + 20 * 60_000).toISOString(),
  slaMinutes: 15,
  priority: 'Low',
  currentBranch: 'Riyadh Central',
  sourceOrderId: 'POS-CASH-91006',
  history: orderHistory('Pending', 'Walk-in cash counter sale created', '2026-07-08 11:50 AM')
}];
export function App() {
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState(screenInit.activeTab || 'dashboard');
  const [orders, setOrders] = useState<Order[]>(INITIAL_ORDERS);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(screenInit.selectedOrderId || null);
  const [newOrderNotification, setNewOrderNotification] = useState<Order | null>(null);
  const [newOrderCreated, setNewOrderCreated] = useState(false);
  const [operationalSettings, setOperationalSettings] = useState<OperationalSettings>(DEFAULT_OPERATIONAL_SETTINGS);
  useEffect(() => {
    if (screenInit.activeTab) setActiveTab(screenInit.activeTab);
    if (screenInit.selectedOrderId !== undefined) setSelectedOrderId(screenInit.selectedOrderId);
  }, [screenInit]);
  useEffect(() => {
    if (newOrderCreated) return;
    const timer = window.setTimeout(() => {
      const slaMinutes = operationalSettings.slaMinutesByChannel['Insurance Platform'];
      const newOrder: Order = {
        id: 'ORD-9901',
        customerName: 'Ahmad Al-Sayed',
        customerPhone: '966505554444',
        customer: {
          name: 'Ahmad Al-Sayed',
          mobile: '966505554444'
        },
        address: {
          line1: 'Al-Rawdah District',
          city: 'Riyadh',
          district: 'Al-Rawdah',
          zipCode: '13211'
        },
        lines: [line({
          itemId: 'l9',
          name: 'Panadol Extra 500mg',
          tradeDrugCode: 'PAN-001',
          genericCode: 'PARA-500',
          qty: 2,
          unitPrice: 92.5
        })],
        itemsCount: 1,
        totalAmount: 185,
        status: 'Pending',
        channel: 'Insurance Platform',
        channelPartner: 'Nafees',
        payerType: 'Insurance',
        paymentType: 'Prepaid',
        createdAt: new Date().toLocaleString(),
        acceptanceDeadline: new Date(Date.now() + slaMinutes * 60_000).toISOString(),
        slaMinutes,
        priority: 'Medium',
        insurance: {
          payerName: 'Tawuniya',
          policyNo: 'TAW-55444',
          memberId: 'MEM-77552',
          copayPercent: 10,
          patientShare: 18.5,
          insuranceShare: 166.5,
          paymentLinkStatus: 'Pending'
        },
        history: orderHistory('Pending', 'Insurance order received', new Date().toLocaleString())
      };
      setOrders(previous => [newOrder, ...previous]);
      setNewOrderCreated(true);
      setNewOrderNotification(newOrder);
      toast.info('New sales order received!');
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [newOrderCreated, operationalSettings.slaMinutesByChannel]);
  useEffect(() => {
    const reassignExpiredOrders = () => {
      const timestamp = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const now = Date.now();
      setOrders(previous => previous.map(order => {
        if (order.status !== 'Pending' || new Date(order.acceptanceDeadline).getTime() > now) return order;
        return {
          ...order,
          status: 'Reassigned',
          history: [...order.history, {
            id: Math.random().toString(36).slice(2, 11),
            timestamp,
            status: 'Reassigned',
            message: 'Acceptance window expired; order reassigned by the routing system',
            actor: 'System',
            actorName: 'Routing System'
          }]
        };
      }));
    };
    reassignExpiredOrders();
    const timer = window.setInterval(reassignExpiredOrders, 1000);
    return () => window.clearInterval(timer);
  }, []);
  const selectedOrder = orders.find(order => order.id === selectedOrderId);
  const handleUpdateStatus = (orderId: string, newStatus: OrderStatus, updates?: Partial<Order>, systemActor = false) => {
    const timestamp = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    setOrders(previous => previous.map(order => {
      if (order.id !== orderId) return order;
      const message = newStatus === 'Rejected' ? `Order rejected and returned to routing: ${updates?.rejectionReason || 'No reason provided'}` : newStatus === 'Searching for Driver' && order.status === 'Delivery Attempt Failed' ? 'Retry delivery initiated; searching for an available driver' : `Status updated to ${newStatus}${updates?.rejectionReason ? `: ${updates.rejectionReason}` : ''}${updates?.holdReason ? `: ${updates.holdReason}` : ''}`;
      const event: HistoryEvent = {
        id: Math.random().toString(36).slice(2, 11),
        timestamp,
        status: newStatus,
        message: systemActor ? `Partner update: ${message}` : message,
        actor: systemActor ? 'System' : 'Pharmacist',
        actorName: systemActor ? 'Partner Integration' : 'Ahmed Al-Farsi'
      };
      return {
        ...order,
        ...updates,
        status: newStatus,
        previousStatus: newStatus === 'Held' ? order.status : order.previousStatus,
        history: [...order.history, event]
      };
    }));
    if (newStatus === 'Rejected') toast.error('Order returned to routing');else if (newStatus === 'Cancelled') toast.error(`Order ${orderId} cancelled`);else if (newStatus === 'Held') toast.warning(`Order ${orderId} held: ${updates?.holdReason || 'No reason provided'}`);else toast.success(`Order ${orderId} status updated to ${newStatus}`);
    if (['Delivered', 'Cancelled', 'Rejected', 'Refunded'].includes(newStatus)) setSelectedOrderId(null);
  };
  return <POSLayout activeTab={activeTab} onTabChange={setActiveTab} data-id="element-0" data-mp="1oi1i50fo6lyf">
    <Toaster position="top-right" expand richColors data-id="element-1" data-mp="1ojun8bamwy4b" />
    <AnimatePresence data-id="element-2" data-mp="1jhvlt6yggba0">{newOrderNotification && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-3" data-mp="1jkrh0nix4lur"><motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} className="w-full max-w-md overflow-hidden rounded-[32px] border border-slate-100 bg-white shadow-2xl" data-id="element-4" data-mp="1elok5qd3dlxv"><div className="space-y-6 p-8 text-center" data-id="element-5" data-mp="3hff57mwjoqx9"><div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-blue-50 text-blue-600" data-id="element-6" data-mp="3hedhahufk9y4"><Package className="h-10 w-10" data-id="element-7" data-mp="3le4eb3ajvmb3" /></div><div data-id="element-8" data-mp="3q777397i6rq8"><h3 className="text-2xl font-black text-slate-900" data-id="element-9" data-mp="3q6ffe9h11e58">New Sales Order</h3><p className="mt-2 text-slate-500" data-id="element-10" data-mp="3q99zfzw4owy4">#{newOrderNotification.id} arrived from {newOrderNotification.channel}.</p></div><div className="divide-y divide-slate-200/50 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-left" data-id="element-11" data-mp="2yg15eoc4wlyu"><SummaryRow label="Customer" value={newOrderNotification.customer.name} data-id="element-12" data-mp="32h3avmwn9u99" /><SummaryRow label="Amount" value={`${newOrderNotification.totalAmount.toFixed(2)} SAR`} data-id="element-13" data-mp="32j674ooe4jmf" /><SummaryRow label="Items" value={`${newOrderNotification.lines.length} items`} data-id="element-14" data-mp="37iist63izknf" /></div><div className="flex gap-3" data-id="element-15" data-mp="3cgu31hxqfx66"><button onClick={() => setNewOrderNotification(null)} className="flex-1 rounded-2xl py-4 font-bold text-slate-500 transition-colors hover:bg-slate-50" data-id="element-16" data-mp="3cd6b2n7xritk">Dismiss</button><button onClick={() => {
                setSelectedOrderId(newOrderNotification.id);
                setNewOrderNotification(null);
                setActiveTab('dashboard');
              }} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-blue-600 py-4 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-17" data-mp="rnqn0003q87m">View Order <ChevronRight className="h-4 w-4" data-id="element-18" data-mp="bykfciuwmjfs" /></button></div></div></motion.div></div>}</AnimatePresence>
    {activeTab === 'dashboard' && !selectedOrderId && <OrderQueue orders={orders} onSelectOrder={order => setSelectedOrderId(order.id)} data-id="element-19" data-mp="3ffj6fcwrj83r" />}
    {activeTab === 'history' && !selectedOrderId && <OrderHistory orders={orders} onSelectOrder={order => setSelectedOrderId(order.id)} data-id="element-20" data-mp="3aiizz0w043c4" />}
    {activeTab === 'settings' && !selectedOrderId && <SettingsPanel settings={operationalSettings} onChange={setOperationalSettings} data-id="element-21" data-mp="3kf5g5cddu67t" />}
    {selectedOrderId && selectedOrder && <OrderDetails order={selectedOrder} settings={operationalSettings} onBack={() => setSelectedOrderId(null)} onUpdateStatus={(status, updates, systemActor) => handleUpdateStatus(selectedOrder.id, status, updates, systemActor)} onUpdateLines={lines => setOrders(previous => previous.map(order => order.id === selectedOrder.id ? {
      ...order,
      lines,
      itemsCount: lines.length
    } : order))} readOnly={activeTab === 'history'} initialShowRejectModal={screenInit.showRejectModal} initialIsBulkScanning={screenInit.isBulkScanning} initialConfirmItem={screenInit.confirmItem} initialShowPartialModal={screenInit.showPartialModal} initialScanningItem={screenInit.scanningItem} initialShowGlobalScanModal={screenInit.showGlobalScanModal} initialShowHoldModal={screenInit.showHoldModal} initialShowOtpModal={screenInit.showOtpModal} data-id="element-22" data-mp="2wormjhf5fzb4" />}
  </POSLayout>;
}
function SummaryRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between py-2" data-id="element-23" data-mp="1ky9bvtz4b8bd"><span className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-24" data-mp="5jburwmww9eh">{label}</span><span className="text-sm font-bold text-slate-700" data-id="element-25" data-mp="9kp3cehe2qp3">{value}</span></div>;
}